import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  buildReservationsReportFilterChips,
  buildReservationsReportFilterOptions,
  countActiveReservationsReportFilters,
  EMPTY_RESERVATIONS_REPORT_FILTERS,
  filterReservationsReportRows,
  mapOrderAvailableToReportRow,
  OrderService,
  OrdersAvailableReportRow,
  removeReservationsReportFilter,
  ReservationsReportFilterChip,
  ReservationsReportFilterOptions,
  ReservationsReportFiltersState
} from 'src/entities/order';
import { OrderPdfDialogComponent } from 'src/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.component';
import { buildOrderPdfDialogConfig } from 'src/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.config';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-reservations-report',
  templateUrl: './reservations-report.component.html',
  styleUrls: ['./styles/reservations-report.layout.css']
})
export class ReservationsReportComponent implements OnInit, OnDestroy {
  isInitialLoading = false;
  isLoadingMore = false;
  loadedCount = 0;

  model: OrdersAvailableReportRow[] = [];
  filteredRows: OrdersAvailableReportRow[] = [];

  filters: ReservationsReportFiltersState = {
    ...EMPTY_RESERVATIONS_REPORT_FILTERS
  };
  filterOptions: ReservationsReportFilterOptions = {
    years: [],
    providers: [],
    reservationStatuses: [],
    orderStatuses: []
  };
  activeFilterChips: ReservationsReportFilterChip[] = [];

  private loadGeneration = 0;

  constructor(
    private readonly orderService: OrderService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    void this.loadReservations();
  }

  ngOnDestroy(): void {
    this.loadGeneration += 1;
  }

  get activeFilterCount(): number {
    return countActiveReservationsReportFilters(this.filters);
  }

  get isBusy(): boolean {
    return this.isInitialLoading || this.isLoadingMore;
  }

  async loadReservations(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.isInitialLoading = true;
    this.isLoadingMore = false;
    this.loadedCount = 0;
    this.model = [];
    this.filteredRows = [];
    this.filterOptions = buildReservationsReportFilterOptions([]);
    this.activeFilterChips = buildReservationsReportFilterChips(this.filters);

    try {
      await this.orderService.loadOrdersAvailablePageProgressively(
        (progress) => {
          if (generation !== this.loadGeneration) {
            return;
          }

          const mapped = progress.items.map(mapOrderAvailableToReportRow);
          this.model = [...this.model, ...mapped];
          this.loadedCount = progress.loadedCount;
          this.filterOptions = buildReservationsReportFilterOptions(this.model);
          this.applyFilters();

          if (progress.pageIndex === 0) {
            this.isInitialLoading = false;
          }
          this.isLoadingMore = progress.hasMore;
        },
        {
          isCancelled: () => generation !== this.loadGeneration
        }
      );

      if (generation === this.loadGeneration) {
        this.isInitialLoading = false;
        this.isLoadingMore = false;
      }
    } catch (error) {
      if (generation !== this.loadGeneration) {
        return;
      }
      console.error('Error cargando reporte de reservas:', error);
      this.model = [];
      this.filteredRows = [];
      this.loadedCount = 0;
      this.activeFilterChips = buildReservationsReportFilterChips(this.filters);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo cargar el reporte de reservas',
        text: 'Intenta actualizar. Si el problema continúa, reinicia con lastKey null.',
        confirmButtonText: 'Entendido'
      });
    } finally {
      if (generation === this.loadGeneration) {
        this.isInitialLoading = false;
        this.isLoadingMore = false;
      }
    }
  }

  onFiltersChange(next: ReservationsReportFiltersState): void {
    this.filters = next;
    this.applyFilters();
  }

  onClearFilters(): void {
    this.filters = { ...EMPTY_RESERVATIONS_REPORT_FILTERS };
    this.applyFilters();
  }

  onRemoveFilter(chipId: ReservationsReportFilterChip['id']): void {
    this.filters = removeReservationsReportFilter(this.filters, chipId);
    this.applyFilters();
  }

  onOpenPdf(row: OrdersAvailableReportRow): void {
    if (!row?.orderId) {
      return;
    }

    this.dialog.open(
      OrderPdfDialogComponent,
      buildOrderPdfDialogConfig({
        orderId: row.orderId,
        orderNumber: row.orderNumber
      })
    );
  }

  private applyFilters(): void {
    this.filteredRows = filterReservationsReportRows(this.model, this.filters);
    this.activeFilterChips = buildReservationsReportFilterChips(this.filters);
  }
}
